import { useContext, useState, useEffect } from "react";
import { UserContext } from "../context/UserContext";
import { User, Mail, Phone, Lock } from "lucide-react";
import api from "../api/axios";
import getInitials from "../utils/getInitials";

export default function ProfilePage() {
  const { user, setUser } = useContext(UserContext);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [fullName, setFullName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");

  useEffect(() => {
    if (user) {
      setFullName(user.name || "");
      setEmail(user.email || "");
      setPhone(user.phone || "");
    }
  }, [user, user?.id]);

  if (!user) return <p className="mt-10 text-center text-slate-300">Loading profile...</p>;

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (savingProfile) return;
    setMessage("");
    setError("");
    setSavingProfile(true);

    try {
      const { data } = await api.put("/profile", { fullName, email, phone });

      setUser({
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        kycStatus: data.kycStatus || user.kycStatus,
      });

      setFullName(data.fullName);
      setEmail(data.email);
      setPhone(data.phone);
      setMessage("Profile updated successfully.");
    } catch (err) {
      const details = err.response?.data?.details;
      setError(details?.length ? details.join(" · ") : err.response?.data?.message || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (savingPassword) return;
    setMessage("");
    setError("");
    setSavingPassword(true);

    try {
      const { data } = await api.put("/profile/password", {
        oldPassword,
        newPassword,
      });

      setMessage(data.message || "Password updated successfully.");
      setOldPassword("");
      setNewPassword("");
    } catch (err) {
      const details = err.response?.data?.details;
      setError(details?.length ? details.join(" · ") : err.response?.data?.message || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <h1 className="text-3xl font-bold">My Profile</h1>
        <p className="mt-1 text-sm text-slate-400">Manage your account details and password securely.</p>
      </section>

      <form
        onSubmit={handleProfileUpdate}
        className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl"
      >
        <div className="mb-5 flex flex-wrap items-center gap-5">
          <div
            aria-label={`${fullName || "User"} initials`}
            className="flex h-20 w-20 items-center justify-center rounded-full border border-cyan-300/25 bg-cyan-300/10 text-2xl font-semibold text-cyan-200"
          >
            {getInitials(fullName)}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-200">Account initials</p>
            <p className="mt-1 text-sm text-slate-400">Generated from your name.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2.5">
            <User size={18} className="mr-2 text-slate-400" />
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full bg-transparent text-white outline-none"
            />
          </label>

          <label className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2.5">
            <Mail size={18} className="mr-2 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent text-white outline-none"
            />
          </label>

          <label className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 md:col-span-2">
            <Phone size={18} className="mr-2 text-slate-400" />
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-transparent text-white outline-none"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={savingProfile}
          aria-busy={savingProfile}
          className="mt-5 rounded-xl bg-accent px-6 py-2.5 font-semibold text-slate-950 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {savingProfile ? "Saving profile…" : "Save Changes"}
        </button>
      </form>

      <form
        onSubmit={handlePasswordUpdate}
        className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl"
      >
        <h2 className="mb-4 text-xl font-bold">Change Password</h2>

        <label className="mb-3 flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2.5">
          <Lock size={18} className="mr-2 text-slate-400" />
          <input
            type="password"
            placeholder="Old password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
          />
        </label>

        <label className="mb-3 flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2.5">
          <Lock size={18} className="mr-2 text-slate-400" />
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
          />
        </label>

        <p className="mb-4 text-xs text-slate-400">New password must include uppercase, lowercase, and a number.</p>

        <button
          type="submit"
          disabled={savingPassword}
          aria-busy={savingPassword}
          className="rounded-xl bg-cyan-300 px-6 py-2.5 font-semibold text-slate-950 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {savingPassword ? "Updating password…" : "Update Password"}
        </button>
      </form>

      {message && (
        <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
          {message}
        </p>
      )}
      {error && (
        <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </p>
      )}
    </div>
  );
}
